import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import MaterialIcon from './MaterialIcon';
import ClothedEyeLoader from './ClothedEyeLoader';
import logger from '../utils/logger.js';
import './ListingManager.css';

// Helper function to decode HTML entities in text
const decodeHtmlEntities = (text) => {
  if (!text) return text;
  return text
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ');
};

const ListingManager = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('displayOrder');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedListing, setSelectedListing] = useState(null);
  const [failedStickers, setFailedStickers] = useState([]);
  const [showFailedStickers, setShowFailedStickers] = useState(false);
  const [loadingFailedStickers, setLoadingFailedStickers] = useState(false);

  // New filter states
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [svgPathFilter, setSvgPathFilter] = useState('Missing SVG Path'); // 'All', 'Has SVG Path', 'Missing SVG Path'
  const [trueScaleFilter, setTrueScaleFilter] = useState('All'); // 'All', 'TRUEscale Only', 'Non-TRUEscale'
  const [categories, setCategories] = useState(['All']);

  useEffect(() => {
    fetchListings();
    fetchCategories();
  }, []);

  const fetchListings = async () => {
    try {
      // Use unified_stickers as the single source of truth and request ALL items
      const response = await fetch('/.netlify/functions/unified-stickers?limit=999&page=1');
      if (response.ok) {
        const data = await response.json();
        setListings(data.data || []);
      } else {
        console.error('Failed to fetch unified listings');
      }
    } catch (error) {
      console.error('Error fetching unified listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      // Get categories from unified stickers table (more comprehensive)
      const response = await fetch('/.netlify/functions/unified-stickers?limit=999&page=1');
      if (response.ok) {
        const data = await response.json();
        const listingData = data.data || [];

        // Extract unique categories and sort them
        const uniqueCategories = [
          ...new Set(
            listingData
              .map((item) => item.category)
              .filter((category) => category && category.trim() !== '')
          ),
        ].sort();

        setCategories(['All', ...uniqueCategories]);
      } else {
        console.error('Failed to fetch categories, using fallback');
        // Fallback to basic categories
        setCategories([
          'All',
          'Animal Stickers',
          'Cryptids',
          'Dinosaur Stickers',
          'Horror',
          'Offroad Slogans',
          'Outdoor Stickers',
          'Police/Military/Fire',
          'Pop Culture Stickers',
          'SVGs',
          'Space Stickers',
          'Vehicle Stickers',
          'Vehicles Pop Culture',
        ]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to basic categories
      setCategories([
        'All',
        'Animal Stickers',
        'Cryptids',
        'Dinosaur Stickers',
        'Horror',
        'Offroad Slogans',
        'Outdoor Stickers',
        'Police/Military/Fire',
        'Pop Culture Stickers',
        'SVGs',
        'Space Stickers',
        'Vehicle Stickers',
        'Vehicles Pop Culture',
      ]);
    }
  };

  const loadFailedStickers = async () => {
    setLoadingFailedStickers(true);
    try {
      // Use the API endpoint to get failed stickers
      const response = await fetch('/.netlify/functions/local-db/failed-stickers');
      if (response.ok) {
        const data = await response.json();
        const failed = data.data || [];

        setFailedStickers(failed);
        setShowFailedStickers(true);

        // DISABLED - using database persistence instead
        logger.log('💾 Save failed stickers disabled - using database persistence');

        logger.log(`📋 Loaded ${failed.length} stickers needing manual dimension updates`);
      } else {
        console.error('Failed to fetch failed stickers from API');
        // Fallback to client-side filtering
        const failed = listings.filter(
          (listing) =>
            !listing.dimensions ||
            listing.dimensions === '' ||
            listing.dimensions === 'null' ||
            listing.dimensions === '{}'
        );

        setFailedStickers(failed);
        setShowFailedStickers(true);

        // DISABLED - using database persistence instead
        logger.log('💾 Save failed stickers disabled - using database persistence');

        logger.log(`📋 Loaded ${failed.length} stickers (fallback method)`);
      }
    } catch (error) {
      console.error('Error loading failed stickers:', error);
      // Fallback to client-side filtering
      const failed = listings.filter(
        (listing) =>
          !listing.dimensions ||
          listing.dimensions === '' ||
          listing.dimensions === 'null' ||
          listing.dimensions === '{}'
      );

      setFailedStickers(failed);
      setShowFailedStickers(true);

      // DISABLED - using database persistence instead
      logger.log('💾 Save failed stickers disabled - using database persistence');

      logger.log(`📋 Loaded ${failed.length} stickers (fallback method)`);
    } finally {
      setLoadingFailedStickers(false);
    }
  };

  const loadSavedFailedStickers = () => {
    // DISABLED - using database persistence instead
    // Load saved failed stickers disabled - using database persistence
    return null;
  };

  const clearFailedStickers = () => {
    setFailedStickers([]);
    setShowFailedStickers(false);
    // DISABLED - using database persistence instead
    logger.log('🗑️ Clear failed stickers disabled - using database persistence');
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const filteredAndSortedListings = listings
    .filter((listing) => {
      // Search filter (title, id, category, tags, SKUs)
      const term = (searchTerm || '').trim().toLowerCase();
      let matchesSearch = term.length === 0;
      if (!matchesSearch && term.length >= 2) {
        // Tags (prefer unified tags field; fallback to parse listing_data)
        let tags = [];
        if (Array.isArray(listing.tags)) {
          tags = listing.tags.map((t) => (t || '').toLowerCase());
        } else if (listing.listing_data && typeof listing.listing_data === 'string') {
          try {
            const ld = JSON.parse(listing.listing_data);
            if (Array.isArray(ld.tags)) tags = ld.tags.map((t) => (t || '').toLowerCase());
          } catch (_) {}
        }

        // SKUs (optional in listing_data)
        let skus = [];
        try {
          if (listing.listing_data && typeof listing.listing_data === 'string') {
            const ld = JSON.parse(listing.listing_data);
            if (Array.isArray(ld.skus)) skus = ld.skus.map((s) => (s || '').toLowerCase());
          }
        } catch (_) {}

        const title = (listing.title || '').toLowerCase();
        const description = (listing.description || '').toLowerCase();
        const category = (listing.category || '').toLowerCase();
        matchesSearch =
          title.includes(term) ||
          description.includes(term) ||
          category.includes(term) ||
          (listing.etsyListingId || '').includes(term) ||
          tags.some((t) => t.includes(term)) ||
          skus.some((s) => s.includes(term));
      }

      // Category filter
      const matchesCategory = selectedCategory === 'All' || listing.category === selectedCategory;

      // SVG Path filter
      let matchesSvgPath = true;
      if (svgPathFilter === 'Has SVG Path') {
        matchesSvgPath = listing.svgPath && listing.svgPath.trim() !== '';
      } else if (svgPathFilter === 'Missing SVG Path') {
        matchesSvgPath = !listing.svgPath || listing.svgPath.trim() === '';
      }

      // TRUEscale filter
      let matchesTrueScale = true;
      if (trueScaleFilter === 'TRUEscale Only') {
        matchesTrueScale = listing.isTrueScale === 1 || listing.isTrueScale === true;
      } else if (trueScaleFilter === 'Non-TRUEscale') {
        matchesTrueScale =
          !listing.isTrueScale || listing.isTrueScale === 0 || listing.isTrueScale === false;
      }

      return matchesSearch && matchesCategory && matchesSvgPath && matchesTrueScale;
    })
    .sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === 'displayOrder') {
        aValue = aValue || 0;
        bValue = bValue || 0;
      } else {
        aValue = aValue || '';
        bValue = bValue || '';
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const handleEditListing = (listing) => {
    setSelectedListing(listing);
    // Navigate to listing admin with this listing ID
    window.location.href = `/listing-admin?listingId=${listing.etsyListingId}`;
  };

  const handleViewListing = (listing) => {
    window.open(listing.etsyUrl, '_blank');
  };

  const getStatusBadge = (listing) => {
    if (!listing.publicVisible) {
      return <span className="status-badge hidden">Hidden</span>;
    }
    if (listing.isSet) {
      return <span className="status-badge pack">Pack</span>;
    }
    return <span className="status-badge visible">Visible</span>;
  };

  const getDimensionStatusBadge = (listing) => {
    if (
      !listing.dimensions ||
      listing.dimensions === '' ||
      listing.dimensions === 'null' ||
      listing.dimensions === '{}'
    ) {
      return <span className="status-badge missing-dimensions">Missing Dimensions</span>;
    }
    return <span className="status-badge has-dimensions">Has Dimensions</span>;
  };

  const getSvgPathStatusBadge = (listing) => {
    if (!listing.svgPath || listing.svgPath.trim() === '') {
      return <span className="status-badge missing-svg">Missing SVG Path</span>;
    }
    return <span className="status-badge has-svg">Has SVG Path</span>;
  };

  if (loading) {
    return (
      <div className="listing-manager">
        <ClothedEyeLoader size="large" showTagline={true} message="Loading listings..." />
      </div>
    );
  }

  const savedFailedStickers = loadSavedFailedStickers();

  return (
    <div className="listing-manager">
      <div className="listing-manager-header">
        <h2>
          <MaterialIcon icon="inventory_2" />
          Listing Manager ({filteredAndSortedListings.length} of {listings.length} listings)
        </h2>

        <div className="listing-manager-controls">
          <div className="search-box">
            <MaterialIcon icon="search" />
            <input
              type="text"
              placeholder="Search listings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Category Filter */}
          <div className="filter-controls">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="filter-select"
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* SVG Path Filter */}
          <div className="filter-controls">
            <select
              value={svgPathFilter}
              onChange={(e) => setSvgPathFilter(e.target.value)}
              className="filter-select"
            >
              <option value="All">All SVG Paths</option>
              <option value="Has SVG Path">Has SVG Path</option>
              <option value="Missing SVG Path">Missing SVG Path</option>
            </select>
          </div>

          {/* TRUEscale Filter */}
          <div className="filter-controls">
            <select
              value={trueScaleFilter}
              onChange={(e) => setTrueScaleFilter(e.target.value)}
              className="filter-select truescale-filter"
              title="Filter by TRUEscale status"
            >
              <option value="All">All TRUEscale</option>
              <option value="TRUEscale Only">TRUEscale Only</option>
              <option value="Non-TRUEscale">Non-TRUEscale</option>
            </select>
          </div>

          <div className="sort-controls">
            <select value={sortBy} onChange={(e) => handleSort(e.target.value)}>
              <option value="displayOrder">Display Order</option>
              <option value="title">Title</option>
              <option value="category">Category</option>
              <option value="etsyListingId">Listing ID</option>
              <option value="isTrueScale">TRUEscale</option>
            </select>
            <button
              className="sort-button"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            >
              <MaterialIcon icon={sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'} />
            </button>
          </div>

          {/* Dimension Management Controls */}
          <div className="dimension-controls">
            <button
              className="dimension-btn load-btn"
              onClick={loadFailedStickers}
              disabled={loadingFailedStickers}
              title="Load stickers missing dimensions"
            >
              <MaterialIcon icon="warning" />
              {loadingFailedStickers ? 'Loading...' : `Load Missing Dimensions`}
            </button>

            {savedFailedStickers && (
              <button
                className="dimension-btn saved-btn"
                onClick={() => {
                  setFailedStickers(savedFailedStickers.stickers);
                  setShowFailedStickers(true);
                }}
                title={`Load saved list from ${new Date(savedFailedStickers.timestamp).toLocaleString()}`}
              >
                <MaterialIcon icon="history" />
                Saved List ({savedFailedStickers.count})
              </button>
            )}

            {showFailedStickers && (
              <button
                className="dimension-btn clear-btn"
                onClick={clearFailedStickers}
                title="Clear failed stickers list"
              >
                <MaterialIcon icon="clear" />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Failed Stickers Section */}
      {showFailedStickers && failedStickers.length > 0 && (
        <div className="failed-stickers-section">
          <div className="failed-stickers-header">
            <h3>
              <MaterialIcon icon="warning" />
              Stickers Needing Manual Dimension Updates ({failedStickers.length})
            </h3>
            <p>
              These stickers failed automatic dimension extraction and need manual entry in the
              admin panel.
            </p>
          </div>

          <div className="listings-table-container">
            <table className="listings-table failed-stickers-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Listing ID</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {failedStickers.map((listing) => (
                  <tr key={listing.etsyListingId} className="listing-row failed-listing-row">
                    <td className="title-cell">
                      <div className="listing-title">{listing.title}</div>
                    </td>
                    <td className="category-cell">{listing.category || 'Uncategorized'}</td>
                    <td className="id-cell">
                      <code>{listing.etsyListingId}</code>
                    </td>
                    <td className="status-cell">{getDimensionStatusBadge(listing)}</td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          className="action-btn view-btn"
                          onClick={() => handleViewListing(listing)}
                          title="View on Etsy"
                        >
                          <MaterialIcon icon="open_in_new" />
                        </button>
                        <button
                          className="action-btn edit-btn"
                          onClick={() => handleEditListing(listing)}
                          title="Edit Listing"
                        >
                          <MaterialIcon icon="edit" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Main Listings Table */}
      <div className="listings-table-container">
        <table className="listings-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('displayOrder')} className="sortable">
                Order
                <MaterialIcon icon="unfold_more" />
              </th>
              <th onClick={() => handleSort('title')} className="sortable">
                Title
                <MaterialIcon icon="unfold_more" />
              </th>
              <th onClick={() => handleSort('category')} className="sortable">
                Category
                <MaterialIcon icon="unfold_more" />
              </th>
              <th onClick={() => handleSort('etsyListingId')} className="sortable">
                Listing ID
                <MaterialIcon icon="unfold_more" />
              </th>
              <th>Status</th>
              <th>Dimensions</th>
              <th>SVG Path</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedListings.map((listing) => (
              <tr key={listing.etsyListingId} className="listing-row">
                <td className="order-cell">{listing.displayOrder || 0}</td>
                <td className="title-cell">
                  <div className="listing-title">{decodeHtmlEntities(listing.title)}</div>
                  <div className="listing-description">
                    {decodeHtmlEntities(listing.description)?.substring(0, 60)}...
                  </div>
                </td>
                <td className="category-cell">{listing.category || 'Uncategorized'}</td>
                <td className="id-cell">
                  <code>{listing.etsyListingId}</code>
                </td>
                <td className="status-cell">{getStatusBadge(listing)}</td>
                <td className="dimensions-cell">{getDimensionStatusBadge(listing)}</td>
                <td className="svg-path-cell">{getSvgPathStatusBadge(listing)}</td>
                <td className="actions-cell">
                  <div className="action-buttons">
                    <button
                      className="action-btn view-btn"
                      onClick={() => handleViewListing(listing)}
                      title="View on Etsy"
                    >
                      <MaterialIcon icon="open_in_new" />
                    </button>
                    <button
                      className="action-btn edit-btn"
                      onClick={() => handleEditListing(listing)}
                      title="Edit Listing"
                    >
                      <MaterialIcon icon="edit" />
                    </button>
                    <button
                      className="action-btn analyze-btn"
                      onClick={() => handleEditListing(listing)}
                      title="Analyze Images"
                    >
                      <MaterialIcon icon="image_search" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredAndSortedListings.length === 0 && (
        <div className="no-listings">
          <MaterialIcon icon="inventory" />
          <p>No listings found matching your search criteria.</p>
        </div>
      )}
    </div>
  );
};

// ListingManager has no props - it's a standalone admin component
ListingManager.propTypes = {};

export default ListingManager;
